/**
 * Route human pointer and keyboard input onto browser commands.
 *
 * A key press carries no coordinates, so it is delivered at the last click
 * point — that is what makes typing land in the field the user just clicked. The
 * point is tagged with its tab and only reused when the tab still matches, so a
 * press can never be delivered at a coordinate measured in another tab's
 * viewport.
 *
 * @module browserSurfaceInput
 */
import type { BrowserCommand, BrowserTabSnapshot } from "@cmmd-forge/contracts";
import type { MutableRefObject } from "react";

import type {
  BrowserSurfaceKeyboardCommand,
  BrowserSurfacePointerCommand,
} from "../../components/browser/BrowserLiveViewSurface";
import type { InteractivePointerPoint } from "./browserShellTypes";

export interface BrowserSurfaceInputTarget {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly enqueueInteractiveBrowserCommand: (command: BrowserCommand) => void;
  readonly lastInteractivePointerPointRef: MutableRefObject<InteractivePointerPoint | null>;
}

export function handleInteractivePointerCommand(
  props: BrowserSurfaceInputTarget,
  command: BrowserSurfacePointerCommand,
): void {
  const tabId = props.activeTab?.tabId;
  if (!tabId) {
    return;
  }
  if (command.type === "clickPoint") {
    props.lastInteractivePointerPointRef.current = {
      tabId,
      x: command.x,
      y: command.y,
    };
    props.enqueueInteractiveBrowserCommand({
      type: "clickPoint",
      tabId,
      x: command.x,
      y: command.y,
      ...(command.button ? { button: command.button } : {}),
    });
    return;
  }
  props.enqueueInteractiveBrowserCommand({
    type: "wheel",
    tabId,
    deltaX: command.deltaX ?? 0,
    deltaY: command.deltaY ?? 0,
    x: command.x,
    y: command.y,
  });
}

export function handleInteractiveKeyboardCommand(
  props: BrowserSurfaceInputTarget,
  command: BrowserSurfaceKeyboardCommand,
): void {
  const tabId = props.activeTab?.tabId;
  if (!tabId) {
    return;
  }
  if (command.type === "typeText" && command.text) {
    props.enqueueInteractiveBrowserCommand({
      type: "typeText",
      tabId,
      text: command.text,
    });
    return;
  }
  if (command.type === "pressKey" && command.key) {
    const pointerPoint = props.lastInteractivePointerPointRef.current;
    props.enqueueInteractiveBrowserCommand({
      type: "pressKey",
      tabId,
      key: command.key,
      ...(pointerPoint?.tabId === tabId ? { x: pointerPoint.x, y: pointerPoint.y } : {}),
    });
  }
}
